// src/pages/BanquetOpsPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

function pickHM(raw) {
  if (!raw) return '';
  const m = String(raw).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

function fmtVenueTime(v) {
  const a = pickHM(v?.START_TIME_STR) || pickHM(v?.START_TIME);
  const b = pickHM(v?.END_TIME_STR) || pickHM(v?.END_TIME);
  if (a && b) return `${a} ~ ${b}`;
  return a || b || '--:--';
}

function fmtEventTime(ev) {
  if (ev?.time_range) return String(ev.time_range).replace('~', '-');
  const a = pickHM(ev?.start_time);
  const b = pickHM(ev?.end_time);
  if (a && b) return `${a} - ${b}`;
  return a || '--:--';
}

export default function BanquetOpsPage() {
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get('t') || '').trim();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [date, setDate] = useState('');
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [seatStatus, setSeatStatus] = useState('available');
  const [busySeat, setBusySeat] = useState(false);
  const [savingIds, setSavingIds] = useState({});

  const authCfg = useMemo(() => ({ params: { t: token } }), [token]);

  const load = useCallback(async () => {
    if (!token) {
      setErr('缺少存取碼（網址需帶 ?t=）');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get('/api/banquet/ops/today', authCfg);
      if (!data?.ok) throw new Error(data?.error || '載入失敗');
      setDate(data.date || '');
      setVenues(Array.isArray(data.venues) ? data.venues : []);
      setEvents(Array.isArray(data.events) ? data.events : []);
      setSeatStatus(data.seat_status === 'full' ? 'full' : 'available');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }, [authCfg, token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const toggleSeat = async () => {
    if (busySeat || !token) return;
    const next = seatStatus === 'full' ? 'available' : 'full';
    setBusySeat(true);
    try {
      const { data } = await axios.post('/api/banquet/ops/seat-status', {
        t: token,
        status: next,
      });
      if (!data?.ok) throw new Error(data?.error || '更新失敗');
      setSeatStatus(next);
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || '更新客滿狀態失敗');
    } finally {
      setBusySeat(false);
    }
  };

  const toggleCheckIn = async (v, nextVal) => {
    const refType = v.refType;
    const refId = Number(v.refId);
    const key = `${refType}:${refId}`;
    if (!refType || !refId || savingIds[key]) return;
    setSavingIds((m) => ({ ...m, [key]: true }));
    try {
      const { data } = await axios.patch('/api/banquet/ops/checkin', {
        t: token,
        refType,
        refId,
        checked_in: !!nextVal,
      });
      if (!data?.ok) throw new Error(data?.error || '更新失敗');
      setVenues((list) => list.map((row) => {
        if (row.refType === refType && Number(row.refId) === refId) {
          return { ...row, checked_in: !!nextVal, CHECKED_IN: nextVal ? 1 : 0 };
        }
        return row;
      }));
      if (nextVal && data.line && data.line.ok === false) {
        alert(`已標記進場，但 LINE 通知失敗：${data.line.error || '未知錯誤'}`);
      }
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || '更新入場失敗');
    } finally {
      setSavingIds((m) => {
        const { [key]: _, ...rest } = m;
        return rest;
      });
    }
  };

  const isFull = seatStatus === 'full';

  return (
    <div style={st.page}>
      <header style={st.header}>
        <div>
          <div style={st.title}>包場營運一覽</div>
          <div style={st.sub}>{date || '—'} · 客滿與入場</div>
        </div>
        <button
          type="button"
          disabled={busySeat || !token}
          onClick={toggleSeat}
          style={{
            ...st.seatBtn,
            background: isFull ? '#dc2626' : '#16a34a',
          }}
        >
          {busySeat ? '處理中…' : (isFull ? '目前客滿（點擊改有空位）' : '目前有空位（點擊改客滿）')}
        </button>
      </header>

      {loading && <div style={st.muted}>載入中…</div>}
      {err && <div style={st.error}>{err}</div>}

      {!loading && !err && (
        <>
          <section style={st.section}>
            <h2 style={st.h2}>今日包場</h2>
            {venues.length === 0 ? (
              <div style={st.muted}>今日無包場</div>
            ) : (
              <div style={st.list}>
                {venues.map((v) => {
                  const checked = !!(v.checked_in || v.CHECKED_IN);
                  const key = `${v.refType}:${v.refId}`;
                  const saving = !!savingIds[key];
                  return (
                    <article
                      key={key}
                      style={{
                        ...st.card,
                        ...(checked ? st.cardIn : null),
                      }}
                    >
                      {checked && (
                        <div style={st.banner}>已進場無需再通知包場人員</div>
                      )}
                      <div style={st.time}>{fmtVenueTime(v)}</div>
                      <div style={st.row}><b>{v.VENUE || '-'}</b></div>
                      <div style={st.row}>
                        {v.CUSTNAME || '-'}
                        {v.PHONE ? ` · ${v.PHONE}` : ''}
                      </div>
                      {v.COUNTER_NOTE && (
                        <div style={st.note}>備註：{v.COUNTER_NOTE}</div>
                      )}
                      {v.activityEquipmentNeed && (
                        <div style={st.note}>設備：{v.activityEquipmentNeed}</div>
                      )}
                      <label style={st.checkLabel}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={saving}
                          onChange={(e) => toggleCheckIn(v, e.target.checked)}
                          style={{ width: 22, height: 22 }}
                        />
                        <span>{checked ? '已入場' : '入場'}</span>
                      </label>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section style={st.section}>
            <h2 style={st.h2}>今日活動</h2>
            {events.length === 0 ? (
              <div style={st.muted}>今日無活動</div>
            ) : (
              <div style={st.list}>
                {events.map((ev) => (
                  <article key={ev.id} style={st.card}>
                    <div style={st.time}>{fmtEventTime(ev)}</div>
                    <div style={st.row}><b>{ev.title || '-'}</b></div>
                    <div style={st.row}>場地：{ev.venue || '-'}</div>
                    <div style={st.row}>負責人：{ev.owner || '-'}</div>
                    {ev.beverage_need && (
                      <div style={st.note}>飲品：{ev.beverage_need}</div>
                    )}
                    {ev.handover_notes && (
                      <div style={st.note}>交接：{ev.handover_notes}</div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const st = {
  page: {
    minHeight: '100dvh',
    background: '#f8fafc',
    padding: '16px 14px 40px',
    fontFamily: 'system-ui, "PingFang TC", "Microsoft JhengHei", sans-serif',
    color: '#0f172a',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: { fontSize: 22, fontWeight: 900 },
  sub: { fontSize: 14, color: '#64748b', marginTop: 2 },
  seatBtn: {
    border: 'none',
    color: '#fff',
    fontWeight: 900,
    fontSize: 15,
    padding: '12px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    minHeight: 48,
  },
  section: { marginBottom: 20 },
  h2: { fontSize: 17, fontWeight: 900, margin: '0 0 10px' },
  list: { display: 'grid', gap: 10 },
  card: {
    background: '#fff',
    border: '2px solid #e2e8f0',
    borderRadius: 12,
    padding: 12,
    display: 'grid',
    gap: 6,
  },
  cardIn: {
    border: '3px solid #16a34a',
    background: '#dcfce7',
  },
  banner: { fontWeight: 900, color: '#166534', fontSize: 14 },
  time: { fontSize: 22, fontWeight: 900 },
  row: { fontSize: 15 },
  note: { fontSize: 14, color: '#334155', whiteSpace: 'pre-wrap' },
  checkLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    fontWeight: 800,
    fontSize: 16,
    cursor: 'pointer',
  },
  muted: { color: '#64748b', fontSize: 14, padding: 8 },
  error: { color: '#b91c1c', fontWeight: 800, padding: 8 },
};
